/**
 * Partner payout checks (`docs/30_PARTNER_PORTAL.md` §8.3): the review queue,
 * one partner's review, decisions, and the providers that check a PAN or a
 * bank account.
 *
 * Every PAN, GSTIN and account number arrives masked — the last four — as it
 * does everywhere; the document itself is the full evidence, opened through
 * the API (and every opening is audited).
 */

import { api } from '@connect/web-shared';

import type { PayoutDetails } from './api';

export type CheckStatus = 'pending' | 'processing' | 'verified' | 'failed' | 'manual_review';
export type DocumentKind = 'pan_card' | 'gst_certificate' | 'bank_proof';
export type Match = 'match' | 'partial' | 'mismatch' | 'not_found' | 'unknown';

export interface QueueRow {
  public_id: string;
  reference: string;
  name: string;
  status: string;
  details_status: PayoutDetails['status'];
  submitted_at: string | null;
  for_review: number;
}

export interface Finding {
  result: Match;
  read: string | null;
}

export interface ReviewDocument {
  public_id: string;
  kind: DocumentKind;
  label: string;
  filename: string;
  content_type: string;
  size: number;
  status: CheckStatus;
  reason: string | null;
  /** Per field (`pan`, `gstin`, `account`, `ifsc`, `name`), or why it was not read. */
  findings: Record<string, Finding | boolean | string>;
  compared_version: number | null;
  checked_at: string | null;
  uploaded_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  superseded_at: string | null;
}

export interface ReviewCheck {
  public_id: string;
  kind: 'ifsc' | 'pan' | 'gstin' | 'bank_account';
  status: CheckStatus;
  reason: string | null;
  result: Record<string, unknown>;
  attempts: number;
  checked_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
}

export interface Review {
  partner: { public_id: string; reference: string; name: string; status: string };
  details: PayoutDetails & { verified_by: string | null };
  required: { kind: DocumentKind; label: string }[];
  checks: ReviewCheck[];
  documents: ReviewDocument[];
  earlier_documents: ReviewDocument[];
  reading_documents: boolean;
  providers: { pan: boolean; gstin: boolean; bank_account: boolean };
}

export interface ProviderField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  help: string | null;
}

export interface ProviderOption {
  key: string;
  label: string;
  kind: ProviderKind;
  note: string | null;
  fields: ProviderField[];
}

export interface ProviderKindSettings {
  available: ProviderOption[];
  provider: string | null;
  provider_label: string | null;
  /** A secret is `"set"`, never its value. */
  values: Record<string, string>;
  updated_at: string | null;
}

export type ProviderKind = 'pan' | 'gstin' | 'bank_account';

export interface ProviderSettings {
  pan: ProviderKindSettings;
  gstin: ProviderKindSettings;
  bank_account: ProviderKindSettings;
  documents: { reader: string | null };
}

export const checkKeys = {
  queue: (state: string) => ['partners', 'checks', 'queue', state] as const,
  review: (id: string) => ['partners', 'checks', 'review', id] as const,
  providers: ['partners', 'checks', 'providers'] as const,
};

const base = '/partners/verification';

export const checksApi = {
  queue: (state: 'waiting' | 'attention') => api.get<QueueRow[]>(`${base}/queue`, { params: { state } }),
  review: (id: string) => api.get<Review>(`${base}/partners/${id}`),
  filePath: (id: string, documentId: string) => `${base}/partners/${id}/documents/${documentId}/file`,
  approve: (id: string, note: string | null) => api.post<Review>(`${base}/partners/${id}/approve`, { note }),
  requestCorrection: (id: string, reason: string, documents: Record<string, string>) =>
    api.post<Review>(`${base}/partners/${id}/request-correction`, { reason, documents }),
  decideDocument: (id: string, documentId: string, verdict: 'verified' | 'failed', reason: string | null) =>
    api.post<Review>(`${base}/partners/${id}/documents/${documentId}/decision`, { verdict, reason }),
  setPanStatus: (id: string, operative: boolean, note: string) =>
    api.put<Review>(`${base}/partners/${id}/pan-status`, { operative, note }),
  providers: () => api.get<ProviderSettings>(`${base}/providers`),
  setProvider: (kind: ProviderKind, provider: string, values: Record<string, string>) =>
    api.put<ProviderSettings>(`${base}/providers/${kind}`, { provider, values }),
  clearProvider: (kind: ProviderKind) => api.delete<ProviderSettings>(`${base}/providers/${kind}`),
};

export const CHECK_STATUS = {
  pending: { label: 'Waiting', tone: 'neutral' as const },
  processing: { label: 'Checking', tone: 'info' as const },
  verified: { label: 'Verified', tone: 'success' as const },
  failed: { label: 'Correction asked', tone: 'danger' as const },
  manual_review: { label: 'For review', tone: 'warning' as const },
};

export const DETAILS_STATUS = {
  none: { label: 'Not given', tone: 'neutral' as const },
  unverified: { label: 'To review', tone: 'warning' as const },
  verified: { label: 'Verified', tone: 'success' as const },
  correction_requested: { label: 'Correction asked', tone: 'danger' as const },
};

export const MATCH = {
  match: { label: 'Matches', tone: 'success' as const },
  partial: { label: 'Partly matches', tone: 'warning' as const },
  mismatch: { label: 'Differs', tone: 'danger' as const },
  not_found: { label: 'Not found on it', tone: 'neutral' as const },
  unknown: { label: 'Unknown', tone: 'neutral' as const },
};

export const CHECK_LABELS = {
  ifsc: 'IFSC (branch directory)',
  pan: 'PAN (government records)',
  gstin: 'GSTIN (GST registration)',
  bank_account: 'Bank account (test credit)',
} as const;

export const FIELD_LABELS: Record<string, string> = {
  pan: 'PAN',
  gstin: 'GSTIN',
  account: 'Account number',
  ifsc: 'IFSC',
  name: 'Name',
};
